<template lang="pug">
.c-directory-child-page
  //- Action buttons for parent slot
  teleport(to=".c-directory-page__nav-actions" v-if="mounted")
    el-button.c-directory-action-btn(@click="exportChannels")
      fai(type="fal" icon="download").me-2
      span Export
    el-button.c-directory-action-btn(@click="dialog = true")
      fai(type="fal" icon="plus-circle").me-2
      span Add New

  .c-directory-content
    //- Filters Section
    section.c-directory-filters-section
      DirectoryFilters(
        v-model="filter"
        placeholder="Search channels..."
        @search="handleSearch"
      )

    //- Table Section
    section.c-directory-table-section
      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--star
            .c-skeleton-cell.c-skeleton-cell--channel
            .c-skeleton-cell.c-skeleton-cell--category
            .c-skeleton-cell.c-skeleton-cell--description
      
      //- Table
      el-table(
        v-else
        :data="filtered"
        @row-click="$router.push(`channels/${$event.id}`)"
        style="width: 100%"
      ).clickable-rows
        el-table-column(width="15")
        
        el-table-column(width="80")
          template(#default="scope")
            div(@click.stop="handleToggleAddToFav(scope.row)").favorite-icon
              img(v-if="scope.row.favorite" src="@/assets/icons/star-green.svg").icon-star
              img(v-else src="@/assets/icons/star.svg").icon-star

        el-table-column(prop="channel" label="Channel" min-width="200")
          template(#default="scope")
            .lh-sm
              .font-black {{ scope.row.channel }}
        
        el-table-column(prop="category" label="Category" min-width="200")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.category ? scope.row.category.category : '' }}
        
        el-table-column(prop="description" label="Description" min-width="250")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.description }}

        el-table-column(width="15")

  dialog-add-channel(
    v-if="dialog"
    :loading="loading"
    v-model="dialog"
    :categories="categories.categories"
    @save-item="saveItem"
    @save-category="saveCategoryItem"
    @close-dialog="dialog = false"
  )
</template>

<script lang="ts">

// eslint-disable-next-line object-curly-newline
import { defineComponent, ref, onMounted, reactive, computed } from 'vue';
import shared from './shared';
import DialogAddChannel from './DialogAddChannel.vue';
import DirectoryFilters from '../DirectoryFilters.vue';
import globalShared from '@/shared';

export default defineComponent({
  name: 'ManageContent_Directory_Channels',
  components: { DialogAddChannel, DirectoryFilters },
  setup() {
    const {
      loading,
      getTypedCategories,
      getChannels,
      saveChannel,
      saveCategory,
      exportData,
    } = shared();

    const { showSuccessMessage } = globalShared();

    // Initialize loading as true to show skeleton immediately
    loading.value = true;

    const mounted = ref(false);
    const filter = ref('');
    const items = reactive({ items: [] });
    const categories = reactive({ categories: [] });
    const dialog = ref(false);

    async function getItems() {
      try {
        loading.value = true;
        items.items = await getChannels(false);
      } catch (err) {
        console.log(err);
      } finally {
        loading.value = false;
      }
    }

    async function saveItem(input) {
      await saveChannel(input);
      showSuccessMessage('Channel added successfully');
      getItems();
    }

    const filtered = computed(() => {
      if (filter.value === '') {
        return items.items;
      }
      const regex = new RegExp(`${filter.value}`, 'gi');
      return items.items.filter((x) => {
        if (x.channel) {
          return x.channel.match(regex);
        }
        return false;
      });
    });

    async function saveCategoryItem(input) {
      await saveCategory(input);
      categories.categories = await getTypedCategories();
    }

    function exportChannels() {
      exportData(items.items);
    }

    function handleSearch() {
      // Search is handled by computed filtered property
    }

    onMounted(async () => {
      mounted.value = true;
      categories.categories = await getTypedCategories();
      await getItems();
    });

    async function handleToggleAddToFav(row) {
      const itemToFav = filtered.value.find((obj) => obj.id === row.id);
      itemToFav.favorite = !itemToFav.favorite;
      await saveChannel({
        id: parseInt(itemToFav.id, 10),
        favorite: itemToFav.favorite,
      });
    }

    return {
      mounted,
      categories,
      items,
      filter,
      filtered,
      loading,
      dialog,
      // methods
      saveItem,
      saveCategoryItem,
      exportChannels,
      handleToggleAddToFav,
      handleSearch,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-directory-child-page {
  width: 100%;
}

.c-directory-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

// Action button styles - Match Invitations page
.c-directory-action-btn {
  height: 36px;
  font-size: 14px;
  border-radius: 8px;
  background-color: white !important;
  border: 1.5px solid $color-purple-text !important;
  color: $color-purple-text !important;

  &:hover,
  &:focus {
    background-color: rgba($color-purple-text, 0.08) !important;
    border-color: color.adjust($color-purple-text, $lightness: -8%) !important;
    color: color.adjust($color-purple-text, $lightness: -8%) !important;
  }

  :deep(span) {
    font-weight: 500;
  }

  :deep(.me-2) {
    margin-right: 0.5rem;
  }
}

.c-directory-filters-section {
  margin-top: 0;
  margin-bottom: 0;
}

.c-directory-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;

  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;

    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

.favorite-icon {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.icon-star {
  width: 24px;
  height: 24px;
}

// Skeleton Loader
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--star {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &--channel {
    width: 180px;
    flex-shrink: 0;
  }

  &--category {
    width: 150px;
    flex-shrink: 0;
  }

  &--description {
    flex: 1;
    min-width: 200px;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
